/**
 * The stateless sections of the booked-shipment page.
 *
 * Each one takes the shipment and renders a section — or renders nothing,
 * because most sections only apply in some shipment states. They hold no state
 * and make no calls; the three sections that do (details, billing, additional
 * options) each live in their own file.
 */

import { ShipmentMap } from "@/Shared/Maps/ShipmentMap";
import type { Invoice } from "@freightsimple/generated-dashboard-openapi-client/models/Invoice";
import type { Shipment } from "@freightsimple/generated-dashboard-openapi-client/models/Shipment";
import type { TrackingInfo } from "@freightsimple/generated-dashboard-openapi-client/models/TrackingInfo";
import { ShipmentState } from "@freightsimple/generated-dashboard-openapi-client/models/ShipmentState";
import { Banner } from "@freightsimple/ui-core/Display/Banner";
import moment from "moment";
import { useShipmentService } from "@/Services/ShipmentService";
import ViewShipmentsJumpBar from "../Bars/ViewShipmentsJumpBar";
import { shipmentIsDeliveringByAppointment } from "@/Shared/Shipment/Helpers/shipmentIsDeliveringByAppointment";
import ShipmentTopBar from "@/Shared/Shipment/Bars/ShipmentTopBar";
import { ViewShipmentsCustomsDocsSection } from "../CustomComponents/ViewShipmentsCustomsDocsSection";
import type { DocumentsSectionItem } from "@/Shared/Shipment/CustomComponents/ShipmentDocumentsSectionGeneric";
import ShipmentDocumentsSection from "@/Shared/Shipment/CustomComponents/ShipmentDocumentsSection";
import ShipmentNextStepsForDeliverySection from "@/Shared/Shipment/CustomComponents/ShipmentNextStepsForDeliverySection";
import ShipmentNextStepsSection from "@/Shared/Shipment/CustomComponents/ShipmentNextStepsSection";
import ShipmentTrackingSection from "@/Shared/Shipment/CustomComponents/ShipmentTrackingSection";

export function viewShipmentsMapFor(shipment: Shipment) {
  return (
    <ShipmentMap
      pickup={shipment.pickupLocation}
      delivery={shipment.deliveryLocation}
      shipmentState={shipment.shipmentState}
    />
  );
}

export function viewShipmentsTopBarFor(shipment: Shipment) {
  const proNumber = shipment.proNumber;
  const billOfLadingNumber = shipment.billOfLadingNumber || undefined;

  const deliveryAppointment = shipment.deliveryLocation.accessorials.includes(
    "SCHEDULING_APPOINTMENT_REQUIRED",
  );

  return (
    <ShipmentTopBar
      shipmentState={shipment.shipmentState}
      pickupLocation={shipment.pickupLocation}
      deliveryLocation={shipment.deliveryLocation}
      pickupDate={shipment.pickupDate}
      pickupHours={shipment.pickupLocation.hours}
      appointmentDate={shipment.appointmentDate}
      expectedDeliveryDate={shipment.expectedDeliveryDate}
      expectedDeliveryHours={shipment.expectedDeliveryHours}
      actualDeliveryDate={shipment.actualDeliveryDate}
      actualDeliveryTime={shipment.actualDeliveryTime}
      predictedLatestDeliveryDate={shipment.latestedPredictedDeliveryDate}
      proNumber={proNumber || "Pending"}
      billOfLadingNumber={billOfLadingNumber}
      deliveryAppointment={deliveryAppointment}
    />
  );
}

export function viewShipmentsJumpBarFor(
  shipment: Shipment,
  bookedByName: string | undefined,
) {
  return (
    <ViewShipmentsJumpBar
      claimsJump={shipment.hasFreightClaims}
      shipmentState={shipment.shipmentState}
      trackingId={shipment.trId}
      bookedByName={bookedByName}
    />
  );
}

export function viewShipmentsCustomsDocsSectionFor(
  shipment: Shipment,
  onUploaded: () => void,
) {
  return (
    <ViewShipmentsCustomsDocsSection
      shipmentId={shipment.shipmentId}
      onUploaded={onUploaded}
    />
  );
}

interface ViewShipmentsNextStepsForShipmentProps {
  shipment: Shipment;
}

export function ViewShipmentsNextStepsForShipment(
  props: ViewShipmentsNextStepsForShipmentProps,
) {
  const { shipment } = props;
  const { downloadPickupPackage } = useShipmentService();

  if (shipment.shipmentState !== ShipmentState.BookingConfirmed) {
    return <></>;
  }

  async function onDownload() {
    await downloadPickupPackage(shipment.shipmentId);
  }

  return <ShipmentNextStepsSection onDownload={onDownload} />;
}

export function ViewShipmentsNextStepsForDeliveryForShipment(
  props: ViewShipmentsNextStepsForShipmentProps,
) {
  const { shipment } = props;

  if (shipment.shipmentState !== ShipmentState.InTransit) {
    return <></>;
  }

  const deliveringByAppointment = shipmentIsDeliveringByAppointment(shipment);

  return (
    <ShipmentNextStepsForDeliverySection
      deliveringByAppointment={deliveringByAppointment}
      appointmentSet={shipment.appointmentDate !== undefined}
    />
  );
}

interface ViewShipmentsFailedPaymentsBannerProps {
  invoices: Invoice[];
}

export function ViewShipmentsFailedPaymentsBanner(
  props: ViewShipmentsFailedPaymentsBannerProps,
) {
  const anyFailedPayments = props.invoices.some(
    (invoice) => invoice.lastPaymentAttemptFailed,
  );

  if (!anyFailedPayments) {
    return <></>;
  }

  return (
    <Banner variant="danger">
      Please see the billing section below to resolve the failed payment. Don't
      worry - your pickup is still booked and the shipment will proceed.
    </Banner>
  );
}

interface ViewShipmentsTrackingSectionForShipmentProps {
  shipment: Shipment;
  trackingInfo: TrackingInfo | undefined;
}

export function ViewShipmentsTrackingSectionForShipment(
  props: ViewShipmentsTrackingSectionForShipmentProps,
) {
  const { shipment, trackingInfo } = props;

  if (!trackingInfo) {
    return <></>;
  }

  const trackingLines = trackingInfo.trackingLines;
  if (!trackingLines || trackingLines.length === 0) {
    return <></>;
  }

  const lines = trackingLines.map((line) => {
    return {
      message: line.message,
      time: moment(line.timestamp),
    };
  });

  return (
    <ShipmentTrackingSection
      shipmentState={shipment.shipmentState}
      lines={lines}
    />
  );
}

interface ViewShipmentsDocumentsSectionForShipmentProps {
  shipment: Shipment;
  documentItems: Array<DocumentsSectionItem>;
  onReload: () => Promise<void>;
}

export function ViewShipmentsDocumentsSectionForShipment(
  props: ViewShipmentsDocumentsSectionForShipmentProps,
) {
  return (
    <ShipmentDocumentsSection
      items={props.documentItems}
      shipmentId={props.shipment.shipmentId}
      onReload={props.onReload}
    />
  );
}
